import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import units from "../units.json"
import { getAudioGraphInstance } from '../audioEngine';
import { SamplerUI } from "../projectViews/stationViews/SamplerUI";
import { sampleLibrary} from "../sampleLibrary";
import { addKeysAsField } from '../utils/misc';
import { limitToNyquist } from '../utils/parameters';
import colors from "../../styles/colors.module.scss";
import { getSampleLibraryManagerInstance, SamplePlayer } from '../SamplePlayer';
import { midiHandler } from '../MidiHandler';

export function getPadParameterName(padIndex, parameterType) {
    return `pad${padIndex + 1}_${parameterType}`;
}

const getInitialStartValue = (numSound, totalSlices=16) => {
    const sliceNum = numSound % totalSlices;
    return sliceNum / totalSlices;
}

const getInitialEndValue = (numSound, totalSlices=16, sliceSpan=1) => {
    const sliceNum = numSound % totalSlices;
    return (sliceNum + sliceSpan) / totalSlices;
}


export class Sampler extends InstrumentBase {
    static metadata = {
        tipus: 'sampler',
        tipusDisplay: 'Sampler',
        versio: '0.1',
        color:  colors.turquoise,
        hasLogo: true,
        hasMiniature: true,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        sound: {type: 'text', initial: 'soulguitar02', bind: "preset"},
        ...Array.from({ length: 16 }).reduce((acc, _, i) => ({
            ...acc,
            [getPadParameterName(i, "volume")]: {type: 'float', unit: units.decibel, min: -60, max: 6, initial: -6, bind: "preset"},
            [getPadParameterName(i, "pan")]: {type: 'float', min: -1, max: 1, initial: 0, bind: "preset"},
            [getPadParameterName(i, "transpose")]: {type: 'float', min: -12, max: 12, step: 1, initial: 0, bind: "preset"},
            [getPadParameterName(i, "start")]: {type: 'float', min: 0, max: 1, initial: getInitialStartValue(i, 8), bind: "preset"},
            [getPadParameterName(i, "end")]: {type: 'float', min: 0, max: 1, initial: getInitialEndValue(i, 8), bind: "preset"},
            [getPadParameterName(i, "isReverse")]: {type: 'bool', initial: false, bind: "preset"},
            [getPadParameterName(i, "envelopeMode")]: {type: 'enum', options: ["gated", "one-shot"], initial: "one-shot", bind: "preset"},
            [getPadParameterName(i, "attack")]: {type: 'float', unit: units.second, min: 0, max: 2, initial: 0, bind: "preset"},
            [getPadParameterName(i, "release")]: {type: 'float', unit: units.second, min: 0, max: 4, initial: 0, bind: "preset"},
        }), {}),
        lpf_cutoff: {type: 'float', unit: units.hertz, min: 200, max: 20000, initial: 20000, logarithmic: true, bind: "preset"},
        choke: {type: 'bool', initial: true, bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        usable: {min: 0, max: 15}
    }
    usePitchShifter = true;

    getUserInterfaceComponent() {
        return SamplerUI
    }

    static maxPolyphony = 3;
    soundBuffers = {};

    getFreeSoundPlayer(padIndex) {
        let soundPlayer = this.audioNodes.soundPlayers.find(soundPlayer => soundPlayer.playingPadIndex === -1);
        // if all are busy, steal the voice from the oldest
        if (soundPlayer === undefined) soundPlayer = this.audioNodes.soundPlayers.sort((a, b) => a.triggerTime - b.triggerTime)[0];
        return soundPlayer;
    }

    getSoundPlayersWithPadPlaying(padIndex) {
        return this.audioNodes.soundPlayers.filter(soundPlayer => soundPlayer.playingPadIndex === padIndex);
    }


    loadSoundInPlayers(soundName) {
        if (!this.audioNodes.hasOwnProperty('soundPlayers')) return;
        this.audioNodes.soundPlayers.forEach(soundPlayer => {
            soundPlayer.player.loadSound(soundName);
        });
    }

    stopAllPlayers(time) {
        if (!this.audioNodes.hasOwnProperty('soundPlayers')) return;
        this.audioNodes.soundPlayers.forEach(soundPlayer => {
            soundPlayer.player.fadeOut = 0;  // Set fade out to 0 to avoid release time
            soundPlayer.player.stop(time);
        });
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const lpf = new Tone.Filter(500, "lowpass", -24, {channelCount: 2});  // Make channel count explicit although it might not be needed (?)

        const soundPlayers = [];
        for (let i = 0; i < this.constructor.maxPolyphony; i++) {
            const panner = new Tone.Panner();
            const player = new SamplePlayer().chain(panner, lpf);
            player.stretchMode = SamplePlayer.STRETCH_MODES.UNSTRETCHED_RESPEED;


            const soundPlayerNodes = {
                playerIdx: i,
                playingPadIndex: -1,
                player,
                panner,
            }
            
            player.player.onstop = () => {
                // After the release time, finally set the sound player as being "free"
                setTimeout(() => {
                    midiHandler.dispatchBrowserMidiEvent(this.id, {pitch: soundPlayerNodes.playingPadIndex, type: 'noteOff'});
                    soundPlayerNodes.playingPadIndex = -1;
                }, player.fadeOut);
                
            }
            soundPlayers.push(soundPlayerNodes)
        }
        
        this.audioNodes = {
            lpf: lpf,
            soundPlayers: soundPlayers,
        };
        
        this.addEffectChainNodes(lpf, this.bus);
        this.isGraphBuilt = true;
    }

    dispose() {
        this.onStopAllSounds();
        this.bus.dispose();
        for (const key of Object.keys(this.audioNodes)) {
            if (key == "effects") {
                for (const key2 of Object.keys(this.audioNodes.effects)) {
                    const node = this.audioNodes.effects[key2];
                    node.dispose();
                }
            } else if (key == "soundPlayers") {
                for (const key2 of Object.keys(this.audioNodes.soundPlayers)) {
                    const soundPlayer = this.audioNodes.soundPlayers[key2];
                    soundPlayer.player.dispose();
                    if (this.usePitchShifter) {
                        soundPlayer.pitchShift.dispose();
                    }
                    soundPlayer.channel.dispose();
                }
            } else {
                const node = this.audioNodes[key];
                node.dispose();
            }
        }
    }

    setPlayerParameter(player, type, value, time) {
        switch (type) {
            case "attack":
                player.player.fadeIn = value;
                break;
            case "release":
                player.player.fadeOut = value;
                break;
            case "volume":
            case "transpose":
            case "start":
            case "end":
            case "speed":
            case "isReverse":
                player.player[type] = value;
                break;
            case "pan":
                player.panner.pan.setValueAtTime(value, time);
        }
    }

    setParameterInAudioGraph(name, value) {
        const parametersMatch = name.match(/^(start|end|attack|release|volume|pan|transpose|speed|isReverse)(\d+)$/);
        if (parametersMatch) {
            const [_, type, indexStr] = parametersMatch;
            const padIndex = parseInt(indexStr, 10) - 1;

            const affectedsoundPlayers = this.getSoundPlayersWithPadPlaying(padIndex);
            affectedsoundPlayers.forEach(soundPlayer => this.setPlayerParameter(soundPlayer, type, value));
        } else if (name == 'lpf_cutoff'){
            this.audioNodes.lpf.frequency.rampTo(limitToNyquist(value), 0.01);
        } else if (name == 'sound') {
            this.loadSoundInPlayers(value);
        }
    }

    triggerPad(padIndex, time, normVelocity=1) {
        // First, stop players if needed
        if (this.getPlaybackParameterValue("choke")) {
            this.stopAllPlayers(time); // immediately stop all if choke is on, as the sampler behaves as if it was mono
        } else {
            this.stopPad(padIndex, time); // immediately stop pad if there's any player playing it
        }
        const soundPlayer = this.getFreeSoundPlayer(padIndex); // todo: this might return unexpected results if the "time" parameter is not 0.
        if (soundPlayer === undefined){
            // If no sound player can be found, don't trigger the sound and return false
            return false;
        }
        ["start", "end", "attack", "release", "pan", "transpose", "isReverse"].forEach(parameterType => {
            const value = this.getPlaybackParameterValue(getPadParameterName(padIndex, parameterType));
            this.setPlayerParameter(soundPlayer, parameterType, value, time);
        });
        const gain = Tone.dbToGain(this.getPlaybackParameterValue(getPadParameterName(padIndex, "volume")));
        soundPlayer.player.gain.gain.setValueAtTime(gain, time);
    
        // If buffer is not loaded yet, don't trigger the sound and return false
        if (!soundPlayer.player.isBufferLoaded()) return false;

        soundPlayer.triggerTime = performance.now();
        soundPlayer.playingPadIndex = padIndex;
        soundPlayer.player.play(time);
        
        return true;
    }
    
    stopPad(padIndex, time) {
        const soundPlayers = this.getSoundPlayersWithPadPlaying(padIndex);
        soundPlayers.forEach(soundPlayer => {
            soundPlayer.player.fadeOut = 0;
            soundPlayer.player.stop(time);
        });
    }
    
    
    triggerAttackRelease(padIndex, time, duration, normVelocity) {
        const triggered = this.triggerPad(padIndex, time, normVelocity);
        // Don't trigger stop if buffer not loaded, this could cause problems
        if (triggered) {
            const isGated = this.getPlaybackParameterValue(getPadParameterName(padIndex, "envelopeMode")) === "gated";
            // Triggering stop will add the fadeOut (release) time.
            if (isGated) this.stopPad(padIndex, time + duration);  
        }
    }
    
    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        let notesDueTrigger = [];
        sequence.forEach(note => note.onset === currentStep && notesDueTrigger.push(note));
        notesDueTrigger = notesDueTrigger.slice(-this.constructor.maxPolyphony); // only keep last maxPolyphony notes to avoid potential problems with start times
        notesDueTrigger.forEach(note => {
            const velocity = (note.velocity ?? 127) / 127;
            const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
            const padIndex = note.pitch % 16;
            midiHandler.dispatchBrowserMidiEvent(this.id, {pitch: padIndex, type: 'noteOn', source: 'sequencer'});
            this.triggerAttackRelease(padIndex, time, duration * getAudioGraphInstance().get16BeatTime(), velocity);
        })
    }

    onTransportStop() {
        // Stop all notes that are still playing
        this.stopAllPlayers()
    }

    onStopAllSounds() {
        // Stop all notes that are still playing
        this.stopAllPlayers()
    }
    
    adjustPitchToInstrumentRange(pitch) {
        return pitch % 16;
    }

    onMidiNote(pitch, midiVelocity, noteOff, extras) {
        if (!getAudioGraphInstance().isGraphBuilt()) return;
        const padIndex = pitch % 16;
        if (!noteOff) this.triggerPad(padIndex, undefined, midiVelocity/127);
        else {
            const isGated = this.getPlaybackParameterValue(getPadParameterName(padIndex, "envelopeMode")) === "gated";
            if (isGated) this.stopPad(padIndex);
        }
        if (!extras.skipRecording) this.handlePianoRollRecording(pitch, midiVelocity, noteOff);
    }
}